import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, Mail, MapPin, ChevronRight, Flame } from "lucide-react";
import Logo from "./Logo";
import { COMPANY } from "../data/site";

const NAV_LINKS = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/services", label: "Services" },
    { to: "/projects", label: "Projects" },
    { to: "/products", label: "Products" },
    { to: "/blogs", label: "Blogs" },
    { to: "/contact", label: "Contact" },
];

const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [location.pathname]);

    return (
        <>
            {/* Top utility bar removed per user request */}

            <header
                className={`sticky top-0 z-50 transition-all duration-300 ${
                    scrolled
                        ? "bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm"
                        : "bg-white border-b border-slate-100"
                }`}
                data-testid="navbar"
            >
                <div className="container-vil flex items-center justify-between py-4">
                    <Link to="/" data-testid="nav-logo-link">
                        <Logo />
                    </Link>

                    <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                end={link.to === "/"}
                                data-testid={`nav-link-${link.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `relative text-sm font-medium tracking-wide transition-colors duration-300 ${
                                        isActive
                                            ? "text-[#E53935]"
                                            : "text-slate-600 hover:text-slate-900"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        {link.label}
                                        {isActive && (
                                            <span className="absolute -bottom-2 left-0 right-0 h-[2px] bg-[#E53935]" />
                                        )}
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center gap-3">
                        <a
                            href={`tel:${COMPANY.primaryPhone.replace(/\s/g, "")}`}
                            className="btn-secondary !py-2.5 !px-4 text-sm"
                            data-testid="nav-call-btn"
                        >
                            <Phone className="w-4 h-4" /> Call Now
                        </a>
                        <Link
                            to="/contact"
                            className="btn-primary !py-2.5 !px-4 text-sm"
                            data-testid="nav-quote-btn"
                        >
                            Get Quotation <ChevronRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <button
                        onClick={() => setOpen(!open)}
                        className="lg:hidden text-slate-900 p-2"
                        data-testid="mobile-menu-toggle"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                {/* Mobile drawer */}
                {open && (
                    <div className="lg:hidden bg-white border-t border-slate-200" data-testid="mobile-nav">
                        <div className="container-vil py-6 flex flex-col gap-4">
                            {NAV_LINKS.map((link) => (
                                <NavLink
                                    key={link.to}
                                    to={link.to}
                                    end={link.to === "/"}
                                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                                    className={({ isActive }) =>
                                        `py-2 text-base border-b border-slate-100 ${
                                            isActive ? "text-[#E53935]" : "text-slate-900"
                                        }`
                                    }
                                >
                                    {link.label}
                                </NavLink>
                            ))}
                            <Link
                                to="/contact"
                                className="btn-primary justify-center mt-4"
                                data-testid="mobile-nav-quote-btn"
                            >
                                Get Quotation
                            </Link>
                        </div>
                    </div>
                )}
            </header>
        </>
    );
};

const Footer = () => {
    return (
        <footer
            className="relative border-t border-slate-200 pt-20 pb-10 overflow-hidden"
            style={{ background: "#0a0a0c" }}
            data-testid="footer"
        >
            <div className="absolute inset-0 grain pointer-events-none" />
            <div className="container-vil relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-14">
                    <div>
                        <Logo dark />
                        <p className="text-slate-400 text-sm leading-relaxed mt-6 max-w-xs">
                            Licensed fire safety engineering company delivering turnkey
                            fire protection & life safety systems across India.
                        </p>
                        <div className="flex items-center gap-2 mt-6 text-xs text-slate-500 font-mono uppercase tracking-widest">
                            <Flame className="w-4 h-4 text-[#E53935]" />
                            Licensed · Trusted · Compliant
                        </div>
                    </div>

                    <div>
                        <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-widest">
                            Quick Links
                        </h4>
                        <ul className="space-y-3 text-sm">
                            {NAV_LINKS.map((l) => (
                                <li key={l.to}>
                                    <Link
                                        to={l.to}
                                        className="text-slate-400 hover:text-[#E53935] transition flex items-center gap-2"
                                        data-testid={`footer-link-${l.label.toLowerCase()}`}
                                    >
                                        <ChevronRight className="w-3 h-3" />
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-widest">
                            Our Services
                        </h4>
                        <ul className="space-y-3 text-sm">
                            {[
                                "Fire Fighting Systems",
                                "Fire Sprinkler Systems",
                                "Fire Alarm Systems",
                                "Fire Suppression",
                                "AMC & Maintenance",
                                "Fire Safety Audits",
                            ].map((s) => (
                                <li key={s} className="text-slate-400">
                                    {s}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-widest">
                            Get in Touch
                        </h4>
                        <div className="space-y-4 text-sm text-slate-400">
                            <div className="flex gap-3">
                                <MapPin className="w-4 h-4 text-[#E53935] mt-0.5 flex-shrink-0" />
                                <span>{COMPANY.address}</span>
                            </div>
                            <div className="flex gap-3">
                                <Phone className="w-4 h-4 text-[#E53935] mt-0.5 flex-shrink-0" />
                                <a
                                    href={`tel:${COMPANY.primaryPhoneRaw}`}
                                    className="text-white hover:text-[#E53935] transition"
                                    data-testid="footer-phone"
                                >
                                    {COMPANY.primaryPhone}
                                </a>
                            </div>
                            <div className="flex gap-3">
                                <Mail className="w-4 h-4 text-[#E53935] mt-0.5 flex-shrink-0" />
                                <a
                                    href={`mailto:${COMPANY.primaryEmail}`}
                                    className="text-white hover:text-[#E53935] transition"
                                    data-testid="footer-email"
                                >
                                    {COMPANY.primaryEmail}
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="divider-glow mb-6" />
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500 font-mono">
                    <p>
                        © {new Date().getFullYear()} VIL Engineering. All rights reserved. ·
                        Licensed Fire Safety Agency
                    </p>
                    <p className="uppercase tracking-widest">
                        Palghar · Boisar · Vasai · Mumbai · MIDC
                    </p>
                </div>
            </div>
        </footer>
    );
};

const StickyButtons = () => {
    const waMsg = encodeURIComponent(
        "Hello VIL Engineering, I'd like to enquire about your fire safety services."
    );
    return (
        <div className="fixed bottom-5 right-5 z-50 flex flex-col gap-3" data-testid="sticky-buttons">
            <a
                href={`https://wa.me/${COMPANY.whatsapp}?text=${waMsg}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-14 h-14 rounded-full flex items-center justify-center text-white wa-pulse transition transform hover:scale-110"
                style={{ background: "#25D366" }}
                aria-label="WhatsApp"
                data-testid="whatsapp-sticky-button"
            >
                <MessageCircle className="w-6 h-6" />
            </a>
            <a
                href={`tel:${COMPANY.primaryPhoneRaw}`}
                className="w-14 h-14 rounded-full flex items-center justify-center text-white transition transform hover:scale-110"
                style={{ background: "#E53935", boxShadow: "0 0 22px rgba(229,57,53,0.55)" }}
                aria-label="Call"
                data-testid="call-floating-button"
            >
                <Phone className="w-6 h-6" />
            </a>
        </div>
    );
};

export const Layout = ({ children }) => {
    return (
        <div className="App">
            <Navbar />
            <main>{children}</main>
            <Footer />
            <StickyButtons />
        </div>
    );
};

export default Layout;
